import { useState } from 'react';

export default function Home() {
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-black to-gray-800 overflow-hidden">
        <div className="container mx-auto px-6 py-20">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left: Text Content */}
            <div className="text-white space-y-6">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight drop-shadow-lg">
                HEARTLAND JUNK HAULERS
              </h1>
              <p className="text-xl md:text-2xl lg:text-3xl font-semibold text-[#16a34a] drop-shadow-md">
                Kansas City's #1 Junk Removal & Dumpster Rental Service
              </p>
              <p className="text-lg md:text-xl text-white/90 drop-shadow-md">
                Fast, affordable junk removal and dumpster rentals for homes and businesses in Kansas City
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap gap-4 pt-4">
                <button 
                  onClick={() => scrollToSection('contact')}
                  className="bg-[#16a34a] text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-[#16a34a] transition-all inline-flex items-center justify-center gap-2 shadow-xl whitespace-nowrap"
                >
                  GET FREE QUOTE
                  <i className="ri-arrow-right-line"></i>
                </button>
                <a 
                  href="tel:+18165410792"
                  className="bg-white text-gray-900 px-8 py-4 rounded-full font-bold text-lg hover:bg-[#16a34a] hover:text-white transition-all inline-flex items-center justify-center gap-2 shadow-xl whitespace-nowrap"
                >
                  <i className="ri-phone-fill"></i>
                  (816) 541-0792
                </a>
              </div>
            </div>

            {/* Right: Image */}
            <div className="relative">
              <img 
                src="https://static.readdy.ai/image/ec7d44241e5d5731ab0de78142a46920/d97199ae15564a7e3cc571f262d98a41.jpeg"
                alt="Heartland Junk Haulers Service"
                className="w-full h-[400px] md:h-[500px] object-cover rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mb-4 text-gray-900">
            OUR SERVICES
          </h2>
          <p className="text-center text-gray-600 text-base md:text-lg mb-16 max-w-2xl mx-auto">
            We handle all types of junk removal and dumpster rentals with professional care
          </p>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-home-4-line',
                title: 'Residential Junk Removal',
                description: 'Clear out your home with our fast and efficient service. From furniture to appliances, we handle it all.'
              },
              {
                icon: 'ri-building-line',
                title: 'Commercial Junk Removal',
                description: 'Keep your business space clean and professional. We work around your schedule for minimal disruption.'
              },
              {
                icon: 'ri-delete-bin-line',
                title: 'Dumpster Rentals',
                description: 'Flexible dumpster rental options for projects of any size. Perfect for renovations, cleanouts, and construction.'
              },
              {
                icon: 'ri-recycle-line',
                title: 'Eco-Friendly Disposal',
                description: 'We prioritize recycling and donation to minimize landfill waste and help the community.'
              },
              {
                icon: 'ri-truck-line',
                title: 'Appliance Removal',
                description: 'Safe removal and disposal of old appliances including refrigerators, washers, and more.'
              },
              {
                icon: 'ri-sofa-line',
                title: 'Furniture Removal',
                description: 'We haul away unwanted furniture of any size, from single items to entire room sets.'
              },
              {
                icon: 'ri-tools-line',
                title: 'Construction Debris',
                description: 'Post-renovation cleanup made easy. We remove all construction waste and debris.'
              },
              {
                icon: 'ri-home-gear-line',
                title: 'Estate Cleanouts',
                description: 'Compassionate and thorough estate cleanout services for Kansas City families.'
              },
              {
                icon: 'ri-store-line',
                title: 'Foreclosure Cleanouts',
                description: 'Fast and complete property cleanouts for foreclosures and rental turnovers.'
              }
            ].map((service, index) => (
              <div key={index} className="bg-gray-50 p-8 rounded-xl hover:shadow-xl transition-all border border-gray-200">
                <div className="w-16 h-16 bg-[#16a34a] rounded-full flex items-center justify-center mb-6">
                  <i className={`${service.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900">{service.title}</h3>
                <p className="text-gray-600 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Dumpster Rentals Section */}
      <section id="dumpster-rentals" className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mb-4 text-gray-900">
            DUMPSTER RENTALS IN KANSAS CITY
          </h2>
          <p className="text-center text-gray-600 text-base md:text-lg mb-16 max-w-3xl mx-auto">
            Need a dumpster rental in Kansas City? We offer flexible rental periods and multiple sizes for residential and commercial projects. From home renovations to construction sites, our dumpster rental service makes waste management easy.
          </p>

          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <img 
                src="https://public.readdy.ai/ai/img_res/566ebef2960775334fa73d0bf21bd2aa.jpg"
                alt="Kansas City Dumpster Rental Services"
                className="w-full h-[400px] object-cover object-top rounded-2xl shadow-xl"
              />
            </div>
            <div className="space-y-6">
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900">
                Flexible Dumpster Rental Options
              </h3>
              <p className="text-gray-600 text-lg leading-relaxed">
                Our Kansas City dumpster rental service provides convenient waste solutions for any project size. Whether you're tackling a home cleanout, renovation, or commercial construction project, we have the right dumpster rental for you.
              </p>
              <ul className="space-y-4">
                {[
                  'Multiple dumpster sizes available',
                  'Flexible rental periods',
                  'Same-day dumpster delivery in Kansas City',
                  'Residential and commercial dumpster rentals',
                  'Competitive dumpster rental pricing',
                  'Easy online booking'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-2xl text-[#16a34a] flex-shrink-0"></i>
                    <span className="text-gray-700 text-lg">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                size: '10 Yard',
                icon: 'ri-inbox-line',
                ideal: 'Small Cleanouts',
                description: 'Perfect for garage cleanouts, small renovation projects, and yard waste removal in Kansas City.'
              },
              {
                size: '20 Yard',
                icon: 'ri-inbox-2-line',
                ideal: 'Medium Projects',
                description: 'Ideal for kitchen remodels, flooring removal, and medium-sized Kansas City home cleanouts.'
              },
              {
                size: '30 Yard',
                icon: 'ri-archive-line',
                ideal: 'Large Renovations',
                description: 'Great for whole-home cleanouts, large construction debris, and major Kansas City renovation projects.'
              },
              {
                size: '40 Yard',
                icon: 'ri-archive-2-line',
                ideal: 'Commercial Jobs',
                description: 'Best for commercial construction, large demolition projects, and major Kansas City estate cleanouts.'
              }
            ].map((dumpster, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-2xl transition-all border-2 border-gray-200">
                <div className="w-20 h-20 bg-[#16a34a] rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${dumpster.icon} text-4xl text-white`}></i>
                </div>
                <h3 className="text-3xl font-bold text-center mb-2 text-gray-900">{dumpster.size}</h3>
                <p className="text-center text-[#16a34a] font-semibold mb-4 text-lg">{dumpster.ideal}</p>
                <p className="text-gray-600 text-center leading-relaxed">{dumpster.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <button 
              onClick={() => scrollToSection('contact')}
              className="bg-[#16a34a] text-white px-10 py-5 rounded-full font-bold text-lg hover:bg-gray-900 transition-all inline-flex items-center gap-3 whitespace-nowrap shadow-xl"
            >
              GET DUMPSTER RENTAL QUOTE
              <i className="ri-arrow-right-line text-xl"></i>
            </button>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mb-16 text-gray-900">
            WHY CHOOSE HEARTLAND?
          </h2>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: 'ri-time-line',
                title: 'Same-Day Service',
                description: 'Need it gone today? We offer same-day pickup for urgent removals.'
              },
              {
                icon: 'ri-money-dollar-circle-line',
                title: 'Transparent Pricing',
                description: 'No hidden fees. You know the cost before we start the job.'
              },
              {
                icon: 'ri-team-line',
                title: 'Professional Team',
                description: 'Trained, insured, and friendly staff who respect your property.'
              },
              {
                icon: 'ri-leaf-line',
                title: 'Eco-Conscious',
                description: 'We recycle and donate whenever possible to reduce waste.'
              }
            ].map((feature, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-20 bg-[#16a34a] rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${feature.icon} text-4xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mb-4 text-gray-900">
            HOW IT WORKS
          </h2>
          <p className="text-center text-gray-600 text-base md:text-lg mb-16 max-w-2xl mx-auto">
            Getting rid of your junk is as easy as 1-2-3
          </p>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-12 max-w-5xl mx-auto">
            {[
              {
                step: '1',
                title: 'Contact Us',
                description: 'Call or fill out our form for a free quote. We respond quickly!'
              },
              {
                step: '2',
                title: 'We Come To You',
                description: 'Schedule a convenient time and our team arrives ready to work.'
              },
              {
                step: '3',
                title: 'Sit Back & Relax',
                description: 'We load, haul, and dispose of everything responsibly. Done!'
              }
            ].map((step, index) => (
              <div key={index} className="text-center relative">
                <div className="w-24 h-24 bg-[#16a34a] rounded-full flex items-center justify-center mx-auto mb-6 text-white text-4xl font-bold shadow-lg">
                  {step.step}
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900">{step.title}</h3>
                <p className="text-gray-600 text-lg">{step.description}</p>
                {index < 2 && (
                  <div className="hidden md:block absolute top-12 left-[60%] w-[80%] h-1 bg-gray-300">
                    <i className="ri-arrow-right-line absolute right-0 top-1/2 -translate-y-1/2 text-2xl text-gray-400"></i>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing/CTA Section */}
      <section id="pricing" className="py-24 bg-gradient-to-br from-gray-900 via-black to-gray-800">
        <div className="relative z-10 text-center max-w-4xl mx-auto px-6">
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-6 leading-tight">
            READY TO CLEAR OUT THE CLUTTER?
          </h2>
          <p className="text-lg md:text-xl text-white/90 mb-10">
            Get your free quote for junk removal or dumpster rental in under 60 seconds
          </p>
          <div className="flex justify-center">
            <button 
              onClick={() => scrollToSection('contact')}
              className="bg-[#16a34a] text-white px-10 py-5 rounded-full font-bold text-lg hover:bg-white hover:text-[#16a34a] transition-all inline-flex items-center gap-3 whitespace-nowrap shadow-xl mb-10"
            >
              GET FREE ESTIMATE NOW
              <i className="ri-arrow-right-line text-xl"></i>
            </button>
          </div>
          <div className="pt-6 border-t border-white/20">
            <p className="text-white/70 text-sm mb-3">Or call us directly:</p>
            <a href="tel:+18165410792" className="text-white text-2xl md:text-3xl font-bold hover:text-[#16a34a] transition-colors inline-flex items-center gap-3">
              <i className="ri-phone-fill"></i>
              (816) 541-0792
            </a>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-3xl">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mb-4 text-gray-900">
            GET YOUR FREE QUOTE
          </h2>
          <p className="text-center text-gray-600 text-base md:text-lg mb-12">
            Fill out the form for junk removal or dumpster rental quotes - we'll respond within minutes
          </p>
          
          <form className="space-y-6 bg-gray-50 p-8 rounded-2xl shadow-lg">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-gray-700 font-semibold mb-2">Name *</label>
                <input 
                  type="text" 
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:border-transparent outline-none"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="block text-gray-700 font-semibold mb-2">Phone *</label>
                <input 
                  type="tel" 
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:border-transparent outline-none"
                  placeholder="(816) 555-0123"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Email *</label>
              <input 
                type="email" 
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:border-transparent outline-none"
                placeholder="your@email.com"
              />
            </div>
            
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Service Address *</label>
              <input 
                type="text" 
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:border-transparent outline-none"
                placeholder="Street address, Kansas City"
              />
            </div>
            
            <div>
              <label className="block text-gray-700 font-semibold mb-2">What needs to be removed? *</label>
              <textarea 
                required
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:border-transparent outline-none resize-none"
                placeholder="Please describe the items you need removed..."
              ></textarea>
            </div>
            
            <button 
              type="submit"
              className="w-full bg-[#16a34a] text-white py-4 rounded-lg font-bold text-lg hover:bg-gray-900 transition-all shadow-lg"
            >
              GET FREE QUOTE NOW
            </button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold mb-4 text-[#16a34a]">HEARTLAND JUNK HAULERS</h3>
              <p className="text-gray-400">
                Kansas City's trusted junk removal service since day one.
              </p>
            </div>
            
            <div>
              <h4 className="text-lg font-bold mb-4">CONTACT</h4>
              <div className="space-y-2 text-gray-400">
                <p className="flex items-center gap-2">
                  <i className="ri-phone-line"></i>
                  (816) 541-0792
                </p>
                <p className="flex items-center gap-2">
                  <i className="ri-map-pin-line"></i>
                  Serving Kansas City & Surrounding Areas
                </p>
              </div>
            </div>
            
            <div>
              <h4 className="text-lg font-bold mb-4">HOURS</h4>
              <div className="space-y-2 text-gray-400">
                <p>Monday - Friday: 7am - 7pm</p>
                <p>Saturday: 8am - 5pm</p>
                <p>Sunday: Closed</p>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400 text-sm">
            <p>&copy; 2025 Heartland Junk Haulers. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
